import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { articleListApi, relatedScrapArticleListApi } from '../apis';

import { clientAxios } from '@/shared/api';
import {
  ApiResponse,
  ArticleListApiResponse,
  ArticleListQuery,
  ArticleListQueryParams,
  FeaturedArticleListQuery,
} from '@/shared/types';
import { convertArticleItem } from '@/shared/utils';

export const useArticleListQuery = ({
  category,
  page,
  ordering,
}: ArticleListQueryParams): UseQueryResult<ArticleListQuery, Error> => {
  return useQuery({
    queryKey: ['articleListApi', { category, page, ordering }],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleListApiResponse>
      >('/articleListApi/', {
        params: { category, page, ordering },
      });

      return {
        maxPage: Math.ceil(data.data.count / 10),
        articles: convertArticleItem(data.data.results),
      };
    },
  });
};

export const useFeaturedArticleListQuery = (): UseQueryResult<
  FeaturedArticleListQuery,
  Error
> => {
  return useQuery({
    queryKey: ['featuredArticleListApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleListApiResponse>
      >('/featuredArticleListApi/');

      const articles = convertArticleItem(data.data.results);

      return {
        maxPage: Math.ceil(data.data.count / 10),
        mainArticle: articles[0],
        subArticles: articles.slice(1, 4),
      };
    },
  });
};

export const usePopularArticleListQuery = (): UseQueryResult<
  ArticleListQuery,
  Error
> => {
  return useQuery({
    queryKey: ['popularArticleListApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleListApiResponse>
      >('/popularArticleListApi/');

      return {
        maxPage: Math.ceil(data.data.count / 10),
        articles: convertArticleItem(data.data.results),
      };
    },
  });
};

export const getThreeRelatedArticles = async (
  category?: string,
  articleId?: string
) => {
  try {
    const data = await articleListApi({ category, page: 1 });

    return convertArticleItem(data.data.results)
      .filter((item) => item.id !== Number(articleId))
      .slice(0, 3);
  } catch (e) {
    console.log(e);
  }
};

export const useGetThreePopularArticles = (articleId?: string) => {
  return useQuery({
    queryKey: ['getThreePopularArticles'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleListApiResponse>
      >('/popularArticleListApi/');

      return convertArticleItem(data.data.results)
        .filter((item) => item.id !== Number(articleId))
        .slice(0, 3);
    },
  });
};

export const useSearchArticleListQuery = ({
  page,
  query,
}: Pick<ArticleListQueryParams, 'query' | 'page'>) => {
  return useQuery({
    retry: 0,
    queryKey: ['searchArticleListApi', query, page],
    queryFn: async () => {
      try {
        const { data } = await clientAxios.get<
          ApiResponse<ArticleListApiResponse>
        >('/searchArticleListApi/', {
          params: { query, page },
        });

        return {
          count: data.data.count,
          maxPage: Math.ceil(data.data.count / 10),
          articles: convertArticleItem(data.data.results),
        };
      } catch (error) {
        return {
          count: 0,
          maxPage: 0,
          articles: [],
        };
      }
    },
  });
};

export const getRelatedScrapArticles = async (id: string) => {
  try {
    const data = await relatedScrapArticleListApi({ id });
    return convertArticleItem(data.data.results).slice(0, 3);
  } catch (e) {
    console.log(e);
  }
};
